import React, {useRef, useState} from 'react';
import DynamicTable from "../DinamicTable/DynamicTable";
import HeaderEntryData from './HeaderEntryData';
import LoadingErrorState from './components/LoadingErrorState';
import { useCitiesAndSpecialties } from './hooks/useCitiesAndSpecialties';
import { useFileUpload } from './hooks/useFileUpload';
import { useDataSave } from './hooks/useDataSave';

/**
 * Main EntryData component - refactored for better maintainability
 * @param {Object} props - Component props
 * @param {string} props.initialHeader - Initial header for the data
 * @param {string} props.cacheName - Cache name for the table
 * @param {boolean} props.optional - Whether this step is optional
 * @param {string} props.instruction - Instructions for the step
 * @param {string} props.helperFileName - Example and attribute file name
 * @returns {JSX.Element} - The EntryData component
 */
const EntryData = ({
  initialHeader,
  cacheName,
  optional = false,
  instruction = "",
  helperFileName
}) => {
  const [tableVersion, setTableVersion] = useState(0);
  const [isInstructionOpen, setIsInstructionOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const originalDataBeforeUpload = useRef(null);

  const {
    completeCities,
    completeSpecialties,
    cities,
    stateGroupings,
    rowsExistent,
    fixedNetworkAdjustmentFactors,
    listOfHints,
    separationLines,
    isLoading,
    error,
    refetch,
    populateRows
  } = useCitiesAndSpecialties(initialHeader);

  const {
    file,
    isUploading,
    handleFileUpload
  } = useFileUpload(initialHeader, completeCities, completeSpecialties, populateRows, setTableVersion);

  const { handleSave } = useDataSave(initialHeader, cities, completeCities, completeSpecialties);

  if (isLoading || error) {
    return (
      <div className="flex flex-col w-full h-screen items-center">
        <LoadingErrorState 
          isLoading={isLoading}
          loaded={loaded}
          error={error} 
          onRetry={refetch} 
        />
      </div>
    );
  }

  if (rowsExistent.length === 0) {
    return (
      <div className="flex flex-col w-full h-screen items-center justify-center">
        <p className="text-gray-500">Nenhum dado encontrado para exibir.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-screen items-center">
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: 'white',
          paddingTop: '16px',
          paddingBottom: '12px',
          width: '100%',
        }}
        className="flex justify-center items-center border-b shadow-sm"
      >
        <HeaderEntryData
          title={initialHeader}
          instruction={instruction}
          optional={optional}
          helperFileName={helperFileName}
          handleFileUpload={handleFileUpload}
          setIsInstructionOpen={setIsInstructionOpen}
          isUploading={isUploading}
          rowsPreload={rowsExistent}
          originalDataBeforeUpload={originalDataBeforeUpload}
        />
      </div>

      <div className="w-full flex-1 min-h-0 overflow-hidden flex flex-col">
        <DynamicTable
          key={`${cacheName}-${tableVersion}`}
          title={initialHeader}
          columnsPreload={cities}
          rowsPreload={rowsExistent}
          onSave={handleSave}
          cacheKey={cacheName}
          stateGroupings={stateGroupings}
          fixedNetworkAdjustmentFactors={fixedNetworkAdjustmentFactors}
          attributeSeparationLines={separationLines}
          optional={optional}
          hints={listOfHints}
          ignoreCache={file !== null}
          isInstructionOpen={isInstructionOpen}
          originalDataBeforeUpload={originalDataBeforeUpload}
          loaded={loaded}
          setLoaded={setLoaded}
        />
      </div>
    </div>
  );
};

export default EntryData;